import { bootstrapEmbed, type BootstrapResponse } from './bootstrap';
import {
   ConfigError,
   MountTargetError,
   SecretKeyInBrowserError,
   StateError,
} from './errors';
import { originOf, resolveHosts, type Environment, type ResolvedHosts } from './environments';
import {
   isIframeMessage,
   MessageType,
   type GateFlow,
   type HostToIframeMessage,
   type InitConfigPayload,
   type PaymentCloseData,
   type PaymentErrorData,
   type PaymentSuccessData,
   type UpdateConfigPayload,
   type WidgetReadyData,
} from './protocol';

export interface GateRampConfig {
   /**
    * Your publishable key (`pk_test_*` / `pk_live_*`). Secret keys are
    * rejected at construction — secret keys MUST stay server-side.
    */
   publishableKey: string;
   /**
    * From `POST /v1/sessions`. Pass to bind this iframe to a session
    * minted server-side. Optional for partners not yet using sessions.
    */
   clientSecret?: string;
   /** `'production'` (default) / `'sandbox'` / `'development'`. */
   environment?: Environment;
   /** Override the API base URL. Power users / self-hosted only. */
   apiBaseUrl?: string;
   /** Override the iframe URL. Power users / self-hosted only. */
   iframeUrl?: string;
   /** Initial theme. Can be flipped at runtime via `setTheme()`. */
   theme?: 'light' | 'dark';
   /** Optional partner constraint passed through to the iframe. */
   targetToken?: string;
   targetNetwork?: string;
   /** Where the iframe redirects on success. Optional when session-bound. */
   returnUrl?: string;
   /**
    * Upgrade 15 — kit-block flow lock. When set, the iframe hides the
    * buy/sell/swap tab strip and forces the user into the chosen flow.
    * Prefer creating the GateSession with `flow` set on your server —
    * this option here is a fallback for partners who haven't moved to
    * sessions yet, OR a local override when you want the SDK to assert
    * the locked flow on top of (or instead of) the session's value.
    */
   flow?: GateFlow;
   /** Pin iframe height (px) — defaults to `600`. */
   height?: number;
   /** Injectable for tests. */
   fetchImpl?: typeof fetch;
}

export interface MountOptions {
   onReady?: (data: WidgetReadyData) => void;
   onSuccess?: (data: PaymentSuccessData) => void;
   onError?: (data: PaymentErrorData) => void;
   onClose?: (data: PaymentCloseData) => void;
   /**
    * Upgrade 14 — fired when the backend says the partner is currently
    * unavailable (e.g. quota exhausted). When this fires, the SDK does
    * NOT mount the iframe — render your own neutral state. The mount
    * promise resolves normally (not rejects); the absence of the iframe
    * is the user-visible signal.
    */
   onUnavailable?: (data: UnavailableData) => void;
}

export interface UnavailableData {
   /** Stable code, e.g. `quota_exhausted`. */
   reason: string;
   /** Free-text version for logging; not for end-user display. */
   message: string;
}

const PK_PREFIX = /^pk_(test|live)_/;
const SK_PREFIX = /^sk_/;

/**
 * Vanilla browser SDK. Stripe.js analog — partners construct one
 * instance per checkout, mount into a container, react to lifecycle
 * events, then unmount on teardown.
 *
 *   const ramp = new GateRamp({
 *     publishableKey: 'pk_test_...',
 *     clientSecret: session.client_secret,  // optional but recommended
 *   });
 *
 *   ramp.mount('#container', {
 *     onSuccess: ({ txId, sessionId }) => router.push('/checkout/done'),
 *     onError:   ({ message }) => toast.error(message),
 *     onClose:   () => analytics.track('checkout_dismissed'),
 *   });
 *
 *   // Later:
 *   ramp.setTheme('light');
 *   ramp.updateConfig({ amount: '250.00' });
 *   ramp.unmount();
 */
export class GateRamp {
   private readonly config: GateRampConfig;
   private readonly hosts: ResolvedHosts;
   private readonly iframeOrigin: string;
   private readonly height: number;

   private iframe: HTMLIFrameElement | null = null;
   private messageListener: ((event: MessageEvent) => void) | null = null;
   private bootstrap: BootstrapResponse | null = null;
   private handlers: MountOptions = {};
   private isReady = false;
   private isMounted = false;

   constructor(config: GateRampConfig) {
      this.config = this.validateConfig(config);
      this.hosts = resolveHosts(this.config.environment, {
         apiBaseUrl: this.config.apiBaseUrl,
         iframeUrl: this.config.iframeUrl,
      });
      this.iframeOrigin = originOf(this.hosts.iframeUrl);
      this.height = config.height ?? 600;
   }

   /**
    * Render the iframe into the container. Returns a promise that
    * resolves when the iframe signals `WIDGET_READY`, or rejects on
    * bootstrap failure / mount target failure.
    */
   async mount(target: string | HTMLElement, options: MountOptions = {}): Promise<void> {
      if (this.isMounted) {
         throw new StateError('GateRamp is already mounted. Call unmount() first.');
      }

      const container = this.resolveContainer(target);
      this.handlers = options;

      // Pre-flight the bootstrap. Surfaces auth/origin failures BEFORE
      // we render a blank iframe.
      this.bootstrap = await bootstrapEmbed({
         apiBaseUrl: this.hosts.apiBaseUrl,
         publishableKey: this.config.publishableKey,
         clientSecret: this.config.clientSecret,
         fetchImpl: this.config.fetchImpl,
      });

      // Upgrade 14 follow-up — when the backend signals the partner is
      // currently unavailable, STILL mount the iframe but forward the
      // unavailable state via INIT_CONFIG so the iframe-app renders its
      // neutral "service unavailable" overlay (spec wants the embed to
      // grey out, not be absent — gives end users a recognisable state
      // instead of a missing element). onUnavailable still fires so
      // partners can layer their own UX on top if they prefer.
      if (this.bootstrap.available === false) {
         this.handlers.onUnavailable?.({
            reason: this.bootstrap.unavailable_reason ?? 'unavailable',
            message: 'Partner is currently unavailable; iframe will render a neutral overlay',
         });
         // fall through — mount the iframe normally; sendInitConfig will
         // include available:false + unavailable_reason so the iframe knows.
      }

      const iframe = document.createElement('iframe');
      iframe.src = this.buildIframeUrl();
      iframe.title = '0Gate Ramp';
      iframe.allow = 'payment *';
      iframe.style.cssText = [
         'border: none',
         'width: 100%',
         'max-width: 440px',
         `height: ${this.height}px`,
         'border-radius: 12px',
         'box-shadow: 0 4px 20px rgba(0,0,0,0.1)',
         'background: transparent',
         'display: block',
         'margin: 0 auto',
      ].join(';');
      iframe.setAttribute('data-0gate', 'ramp');

      // Listener BEFORE inserting the iframe — protects against the
      // (rare) case where the iframe fires WIDGET_READY synchronously.
      this.messageListener = event => this.onMessage(event);
      window.addEventListener('message', this.messageListener);

      container.appendChild(iframe);
      this.iframe = iframe;
      this.isMounted = true;

      return new Promise<void>((resolve, reject) => {
         const readyTimeout = setTimeout(() => {
            reject(new Error('Iframe did not signal WIDGET_READY within 15s'));
         }, 15_000);

         const originalReady = this.handlers.onReady;
         this.handlers = {
            ...this.handlers,
            onReady: data => {
               clearTimeout(readyTimeout);
               originalReady?.(data);
               resolve();
            },
         };
      });
   }

   /** Tear down the iframe + listeners. Safe to call when not mounted. */
   unmount(): void {
      if (!this.isMounted) return;
      if (this.messageListener) {
         window.removeEventListener('message', this.messageListener);
         this.messageListener = null;
      }
      if (this.iframe?.parentNode) {
         this.iframe.parentNode.removeChild(this.iframe);
      }
      this.iframe = null;
      this.handlers = {};
      this.isReady = false;
      this.isMounted = false;
      this.bootstrap = null;
   }

   /** Swap the iframe theme at runtime. */
   setTheme(theme: 'light' | 'dark'): void {
      this.send({ type: MessageType.SET_THEME, data: { theme } });
   }

   /**
    * Upgrade 12 — Hosted-redirect mode (Stripe `redirectToCheckout` analog).
    *
    * For partners on platforms that can't iframe-embed (native mobile
    * webviews, Twitter / Telegram in-app browsers, partner sites with
    * a hostile CSP). Sends the user to a hosted page on our widget
    * origin where they complete the flow, then `return_url` (set on
    * the session at create time) takes them back.
    *
    *   GateRamp.redirectToCheckout({
    *     publishableKey: 'pk_test_...',
    *     clientSecret: session.client_secret,  // REQUIRED — hosted mode
    *                                            // is always session-bound
    *   });
    *
    * No instance needed — this is a one-shot navigation. The function
    * returns void; the page unloads as soon as `window.location.assign`
    * is called.
    */
   static redirectToCheckout(input: {
      publishableKey: string;
      clientSecret: string;
      environment?: Environment;
      iframeUrl?: string;
      theme?: 'light' | 'dark';
   }): void {
      if (!input.publishableKey || typeof input.publishableKey !== 'string') {
         throw new ConfigError('publishableKey is required and must be a string');
      }
      if (SK_PREFIX.test(input.publishableKey)) {
         throw new SecretKeyInBrowserError();
      }
      if (!PK_PREFIX.test(input.publishableKey)) {
         throw new ConfigError(
            'publishableKey must start with `pk_test_` or `pk_live_`',
         );
      }
      if (!input.clientSecret || typeof input.clientSecret !== 'string') {
         throw new ConfigError(
            'clientSecret is required for hosted-redirect mode — mint a session server-side first',
         );
      }

      const hosts = resolveHosts(input.environment, { iframeUrl: input.iframeUrl });
      const url = new URL(hosts.iframeUrl);
      url.searchParams.set('client_secret', input.clientSecret);
      url.searchParams.set('publishableKey', input.publishableKey);
      if (input.theme) url.searchParams.set('theme', input.theme);

      window.location.assign(url.toString());
   }

   /** Push a partial config update (amount, currency, etc.). */
   updateConfig(update: UpdateConfigPayload): void {
      this.send({ type: MessageType.UPDATE_CONFIG, data: update });
   }

   // ---- internals ---------------------------------------------------

   private validateConfig(config: GateRampConfig): GateRampConfig {
      if (!config || typeof config !== 'object') {
         throw new ConfigError('GateRamp config is required');
      }
      if (!config.publishableKey || typeof config.publishableKey !== 'string') {
         throw new ConfigError('publishableKey is required and must be a string');
      }
      if (SK_PREFIX.test(config.publishableKey)) {
         throw new SecretKeyInBrowserError();
      }
      if (!PK_PREFIX.test(config.publishableKey)) {
         throw new ConfigError(
            'publishableKey must start with `pk_test_` or `pk_live_`',
         );
      }
      if (config.clientSecret !== undefined && typeof config.clientSecret !== 'string') {
         throw new ConfigError('clientSecret must be a string when provided');
      }
      if (config.height !== undefined && (typeof config.height !== 'number' || config.height <= 0)) {
         throw new ConfigError('height must be a positive number when provided');
      }
      return config;
   }

   private resolveContainer(target: string | HTMLElement): HTMLElement {
      if (typeof target === 'string') {
         const el = document.querySelector(target);
         if (!el) {
            throw new MountTargetError(`Mount target "${target}" not found in DOM`);
         }
         if (!(el instanceof HTMLElement)) {
            throw new MountTargetError(`Mount target "${target}" is not an HTMLElement`);
         }
         return el;
      }
      if (!(target instanceof HTMLElement)) {
         throw new MountTargetError('Mount target must be a selector string or HTMLElement');
      }
      return target;
   }

   private buildIframeUrl(): string {
      const url = new URL(this.hosts.iframeUrl);
      if (this.config.theme) url.searchParams.set('theme', this.config.theme);
      return url.toString();
   }

   private onMessage(event: MessageEvent): void {
      // Strict origin gate — only trust messages from the iframe origin
      // we just loaded. Anything else is either a different widget on
      // the page or a malicious sender.
      if (event.origin !== this.iframeOrigin) return;
      if (event.source && this.iframe && event.source !== this.iframe.contentWindow) return;

      if (!isIframeMessage(event.data)) return;
      const message = event.data;

      switch (message.type) {
         case MessageType.WIDGET_READY:
            this.isReady = true;
            this.sendInitConfig();
            this.handlers.onReady?.(message.data ?? {});
            break;
         case MessageType.PAYMENT_SUCCESS:
            this.handlers.onSuccess?.(message.data);
            break;
         case MessageType.PAYMENT_ERROR:
            this.handlers.onError?.(message.data);
            break;
         case MessageType.PAYMENT_CLOSE:
            this.handlers.onClose?.(message.data);
            break;
      }
   }

   private sendInitConfig(): void {
      const payload: InitConfigPayload = {
         publishableKey: this.config.publishableKey,
         clientSecret: this.config.clientSecret,
         targetToken: this.config.targetToken ?? this.bootstrap?.target_token ?? undefined,
         targetNetwork: this.config.targetNetwork ?? this.bootstrap?.target_network ?? undefined,
         returnUrl: this.config.returnUrl ?? this.bootstrap?.return_url ?? undefined,
         amount: this.bootstrap?.amount ?? undefined,
         currency: this.bootstrap?.currency ?? undefined,
         // Upgrade 14 follow-up: forward availability so the iframe-app
         // can render the unavailable overlay. Omitted when bootstrap
         // didn't set the field (older backends).
         available: this.bootstrap?.available,
         unavailable_reason: this.bootstrap?.unavailable_reason ?? null,
         // Upgrade 15: kit-block flow lock. Local config wins (lets
         // partners explicitly assert a flow), then we fall back to the
         // session-bound value forwarded from bootstrap.
         flow: this.config.flow ?? this.bootstrap?.flow ?? null,
         // Upgrade 16: enterprise data passthrough. All sourced from
         // the session — never set locally because the server is the
         // source of truth (legal sign-off on kyc_pre_verified, etc.).
         kyc_pre_verified: this.bootstrap?.kyc_pre_verified ?? false,
         walletAddress: this.bootstrap?.wallet_address ?? undefined,
         userReference: this.bootstrap?.user_reference ?? undefined,
      };
      this.send({ type: MessageType.INIT_CONFIG, data: payload });
   }

   private send(message: HostToIframeMessage): void {
      if (!this.iframe?.contentWindow) {
         // Drop silently — likely called before mount() or after unmount().
         // No throw because partners chain methods speculatively.
         return;
      }
      this.iframe.contentWindow.postMessage(message, this.iframeOrigin);
   }

   // ---- test-only inspectors (do not document publicly) ------------

   /** @internal */
   get _iframeForTest(): HTMLIFrameElement | null {
      return this.iframe;
   }
   /** @internal */
   get _isReadyForTest(): boolean {
      return this.isReady;
   }
}
